import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
    Phone,
    Briefcase,
    Calendar,
    Edit,
    Trash2,
    ArrowLeft,
    User,
    Mail,
    Award,
} from 'lucide-react';
import SummaryApi from '../../common';
import EmployeeForm from '../../components/EmployeeForm';
import AttendanceModal from '../../components/AttendanceModal';
import fetchWithAuth from '../../common/fetchWithAuth';

const EmployeeDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [employee, setEmployee] = useState(location.state?.employee || null);
    const [loading, setLoading] = useState(!location.state?.employee);
    const [error, setError] = useState("");
    const [showEditForm, setShowEditForm] = useState(false);

    const [showAttendanceModal, setShowAttendanceModal] = useState(false);


    const fetchEmployeeDetails = async () => {
        try {
            setLoading(true);
            const url = SummaryApi.getStaffById.url(id);
            const res = await fetchWithAuth(url, {
                method: SummaryApi.getStaffById.method,
            });
            const data = await res.json();
            if (!data.success && !data.data && !data.staff) throw new Error(data.message || "Failed to load employee details");
            setEmployee(data.data || data.staff);
            setError("");
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load employee details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!employee && id) {
            fetchEmployeeDetails();
        }
    }, [id, employee]);

    const onDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this employee? This action cannot be undone.")) return;
        try {
            const url = SummaryApi.deleteStaff.url(id);
            const res = await fetchWithAuth(url, {
                method: SummaryApi.deleteStaff.method,
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || "Delete failed");
            navigate('/dashboard/employees');
        } catch (err) {
            console.error(err);
            alert(err.message || "Delete failed");
        }
    };

    const onEditSuccess = (updatedEmployee) => {
        setShowEditForm(false);
        if (updatedEmployee) {
            setEmployee(updatedEmployee);
        } else {
            fetchEmployeeDetails();
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-gray-500">Loading employee details...</div>
        </div>
    );

    if (error) return (
        <div className="p-6">
            <div className="bg-rose-50 border border-rose-100 text-rose-600 p-4 rounded-xl flex items-center gap-3">
                <span>{error}</span>
                <Link to="/dashboard/employees" className="underline font-medium">Go back</Link>
            </div>
        </div>
    );

    if (!employee) return (
        <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
            <User size={48} className="mb-4 text-gray-300" />
            <p className="text-lg">Employee not found.</p>
            <Link to="/dashboard/employees" className="mt-4 text-emerald-600 hover:underline">Return to list</Link>
        </div>
    );

    return (
        <div className="w-full pb-10">
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={() => navigate('/dashboard/employees')}
                    className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors mb-4"
                >
                    <ArrowLeft size={20} />
                    <span>Back to List</span>
                </button>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                            {employee.name ? employee.name.charAt(0).toUpperCase() : <User />}
                        </div>
                        {employee.name}
                    </h1>
                    <div className="flex items-center gap-3">
                        {/* <button
                            onClick={() => setShowAttendanceModal(true)}
                            className="flex items-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            <Calendar size={18} />
                            Mark Attendance
                        </button> */}
                        <button
                            onClick={() => setShowEditForm(true)}
                            className="flex items-center gap-2 bg-amber-100 text-amber-600 hover:bg-amber-200 px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            <Edit size={18} />
                            Edit
                        </button>
                        <button
                            onClick={onDelete}
                            className="flex items-center gap-2 bg-rose-100 text-rose-600 hover:bg-rose-200 px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            <Trash2 size={18} />
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Main Info Card */}
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                            <User size={20} className="text-gray-400" />
                            Personal Information
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Full Name</label>
                                <div className="text-gray-900 font-medium text-lg">{employee.name}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
                                <div className="flex items-center gap-2 text-gray-900 font-medium">
                                    <Phone size={16} className="text-gray-400" />
                                    {employee.phone || "Not provided"}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                                <div className="flex items-center gap-2 text-gray-900 font-medium">
                                    <Mail size={16} className="text-gray-400" />
                                    {employee.email || "Not provided"}
                                </div>
                            </div>


                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                            <Briefcase size={20} className="text-gray-400" />
                            Employment Details
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Designation</label>
                                <div className="text-gray-900 font-medium">{employee.designation || "Not specified"}</div>
                            </div>



                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Joining Date</label>
                                <div className="flex items-center gap-2 text-gray-900 font-medium">
                                    <Calendar size={16} className="text-gray-400" />
                                    {employee.joiningDate
                                        ? new Date(employee.joiningDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                        : "Not specified"}
                                </div>
                            </div>



                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Salary</label>
                                <div className="text-gray-900 font-medium">
                                    ₹ {employee.salary ? Number(employee.salary).toLocaleString() : "0"}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Skills Section */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                            <Award size={20} className="text-gray-400" />
                            Skills
                        </h2>

                        <div>
                            {employee.skillSet && employee.skillSet.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {Array.isArray(employee.skillSet) ? (
                                        employee.skillSet.map((skill, index) => (
                                            <span
                                                key={index}
                                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium border border-gray-200"
                                            >
                                                {skill}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-gray-900">{employee.skillSet}</span>
                                    )}
                                </div>
                            ) : (
                                <div className="text-gray-400 italic">No skills added</div>
                            )}
                        </div>
                    </div>
                </div>



            </div>


            {/* Modals */}
            {
                showEditForm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm">
                        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                                <h3 className="text-lg font-bold text-gray-800">Edit Employee</h3>
                                <button
                                    onClick={() => setShowEditForm(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="p-6">
                                <EmployeeForm
                                    initialData={employee}
                                    onSuccess={onEditSuccess}
                                    onCancel={() => setShowEditForm(false)}
                                />
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showAttendanceModal && (
                    <AttendanceModal
                        staff={employee}
                        onClose={() => setShowAttendanceModal(false)}
                        onSuccess={() => { }}
                    />
                )
            }
        </div >
    );
};

export default EmployeeDetails;
